import React, { Suspense, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useXRInputSourceState, useXRInputSourceEvent } from '@react-three/xr';
import { useSimStore } from '../state/store';

const WIDTH = 1.15;

// Pulse a controller's haptic actuator (no-op on hand-tracking devices).
function pulse(state, intensity = 0.7, ms = 90) {
  try {
    const gp = state && state.inputSource && state.inputSource.gamepad;
    const act = gp && gp.hapticActuators && gp.hapticActuators[0];
    if (act && act.pulse) act.pulse(intensity, ms);
  } catch (e) {
    /* haptics unavailable */
  }
}

// Maps Vision Pro pinch + Quest grab gestures onto the interactive deck & nodes.
// Pinch/grab already dispatch R3F pointer events onto the sliders and hotspots
// (so the actual manipulation works out of the box in @react-three/xr v6); this
// bridge reads the live input-source state to add tactile haptic confirmation.
function XRGestureBridge() {
  const ctrlL = useXRInputSourceState('controller', 'left');
  const ctrlR = useXRInputSourceState('controller', 'right');
  const handL = useXRInputSourceState('hand', 'left');
  const handR = useXRInputSourceState('hand', 'right');
  const prev = useRef({ l: false, r: false });

  const grabbing = (c) =>
    !!c &&
    !!c.gamepad &&
    ((c.gamepad['xr-standard-squeeze'] && c.gamepad['xr-standard-squeeze'].state === 'pressed') ||
      (c.gamepad['xr-standard-trigger'] && c.gamepad['xr-standard-trigger'].state === 'pressed'));

  useFrame(() => {
    const l = grabbing(ctrlL);
    const r = grabbing(ctrlR);
    if (l && !prev.current.l) pulse(ctrlL);
    if (r && !prev.current.r) pulse(ctrlR);
    prev.current.l = l;
    prev.current.r = r;
  });

  // Vision Pro pinch (and hand select) fire 'selectstart' — confirm with a pulse.
  useXRInputSourceEvent(
    'all',
    'selectstart',
    () => {
      pulse(ctrlL, 0.55, 70);
      pulse(ctrlR, 0.55, 70);
    },
    []
  );

  // keep the hooks referenced so hand tracking stays subscribed
  void handL;
  void handR;
  return null;
}

// Text wrapped in a local Suspense so async font loading never blanks the scene.
const STxt = (props) => (
  <Suspense fallback={null}>
    <Text {...props} />
  </Suspense>
);

function Slider3D({ rowY, min, max, step, value, onChange, color, label, format }) {
  const [drag, setDrag] = useState(false);
  const setDragging = useSimStore((s) => s.setDragging);

  const t = (value - min) / (max - min);
  const hx = -WIDTH / 2 + t * WIDTH;

  const apply = (e) => {
    const local = THREE.MathUtils.clamp((e.point.x + WIDTH / 2) / WIDTH, 0, 1);
    let v = min + local * (max - min);
    v = Math.round(v / step) * step;
    v = THREE.MathUtils.clamp(v, min, max);
    onChange(v);
  };

  const start = (e) => {
    e.stopPropagation();
    setDrag(true);
    setDragging(true);
    apply(e);
  };
  const move = (e) => {
    if (drag) {
      e.stopPropagation();
      apply(e);
    }
  };
  const end = () => {
    if (drag) {
      setDrag(false);
      setDragging(false);
    }
  };

  return (
    <group position={[0, rowY, 0]}>
      <STxt position={[-WIDTH / 2, 0.075, 0.02]} fontSize={0.045} color="#c7d2dd" anchorX="left" anchorY="middle" letterSpacing={0.12}>
        {label}
      </STxt>
      <STxt position={[WIDTH / 2, 0.075, 0.02]} fontSize={0.05} color={color} anchorX="right" anchorY="middle">
        {format(value)}
      </STxt>

      {/* invisible interaction plane (click + drag) */}
      <mesh onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerOut={end}>
        <planeGeometry args={[WIDTH + 0.2, 0.22]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} colorWrite={false} />
      </mesh>

      {/* track */}
      <mesh position={[0, 0, 0.005]}>
        <boxGeometry args={[WIDTH, 0.006, 0.006]} />
        <meshBasicMaterial color="#41505f" transparent opacity={0.8} />
      </mesh>
      {/* fill */}
      <mesh position={[-WIDTH / 2 + (hx + WIDTH / 2) / 2, 0, 0.007]}>
        <boxGeometry args={[Math.max(0.001, hx + WIDTH / 2), 0.01, 0.01]} />
        <meshBasicMaterial color={color} />
      </mesh>
      {/* handle (pinch/grab friendly) */}
      <mesh position={[hx, 0, 0.02]} onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerOut={end}>
        <boxGeometry args={[0.06, 0.12, 0.06]} />
        <meshStandardMaterial color="#ffffff" emissive={color} emissiveIntensity={0.7} metalness={0.2} roughness={0.4} />
      </mesh>
    </group>
  );
}

// Floating in-world control deck: works with a mouse on desktop and with VR
// controller rays in headset (both dispatch R3F pointer events).
export default function VRControls() {
  const wager = useSimStore((s) => s.wager);
  const parlay = useSimStore((s) => s.parlay);
  const setWager = useSimStore((s) => s.setWager);
  const setParlay = useSimStore((s) => s.setParlay);

  return (
    <group position={[0, 0.95, 0.55]}>
      <XRGestureBridge />
      {/* glass backing */}
      <mesh position={[0, 0, -0.01]}>
        <boxGeometry args={[WIDTH + 0.35, 0.44, 0.02]} />
        <meshStandardMaterial color="#0b1016" transparent opacity={0.72} metalness={0.3} roughness={0.5} />
      </mesh>
      <STxt position={[-WIDTH / 2 - 0.05, 0.16, 0.02]} fontSize={0.04} color="#7f8ea0" anchorX="left" anchorY="middle" letterSpacing={0.22}>
        SPATIAL DECK
      </STxt>

      <Slider3D
        rowY={0.03}
        min={1}
        max={5000}
        step={1}
        value={wager}
        onChange={setWager}
        color="#00e0d0"
        label="WAGER"
        format={(v) => `$${Math.round(v).toLocaleString()}`}
      />
      <Slider3D
        rowY={-0.11}
        min={1}
        max={12}
        step={1}
        value={parlay}
        onChange={setParlay}
        color="#e8c020"
        label="PARLAY"
        format={(v) => `${v} ${v === 1 ? 'LEG' : 'LEGS'}`}
      />
    </group>
  );
}
